import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/d9v9rjmth95mubjtu4tg";

interface SubmitStatus {
  type: "success" | "error";
  message: string;
}

export default function ContactForm() {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<SubmitStatus | null>(null);
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 蜜罐字段检测：若被机器人填写，则假装提交成功但不发送
    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus({ type: "success", message: "提交成功，我们会尽快与您联系！" });
      setFormError("");
      form.reset();
      return;
    }
    formData.delete("contact_alt");

    setSubmitting(true);
    setStatus(null);
    setFormError("");

    const params = new URLSearchParams();
    formData.forEach((value, key) => params.append(key, String(value)));

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === "OK") {
        setStatus({ type: "success", message: serverMessage || "提交成功，我们会尽快与您联系！" });
        setFormError("");
        form.reset();
      } else {
        setFormError(serverMessage || "提交失败，请稍后重试");
        setStatus({ type: "error", message: "提交失败，请检查后重新提交" });
      }
    } catch {
      setFormError("网络异常，请稍后重试");
      setStatus({ type: "error", message: "网络异常，请稍后重试" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-readdy-form="178漫画网联系表单"
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
      noValidate
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-name" className="text-sm font-medium text-foreground-950">
            您的称呼 <span className="text-primary-600">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            maxLength={50}
            placeholder="请输入您的称呼"
            className="h-11 rounded-md border border-background-200 bg-background-100 px-3.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-email" className="text-sm font-medium text-foreground-950">
            电子邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            maxLength={100}
            placeholder="请输入您的邮箱"
            className="h-11 rounded-md border border-background-200 bg-background-100 px-3.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="contact-subject" className="text-sm font-medium text-foreground-950">
          留言主题
        </label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          maxLength={100}
          placeholder="请简要描述您的问题或建议"
          className="h-11 rounded-md border border-background-200 bg-background-100 px-3.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="contact-message" className="text-sm font-medium text-foreground-950">
          留言内容 <span className="text-primary-600">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={5}
          maxLength={500}
          placeholder="请详细描述您想咨询的内容（最多500字）"
          className="w-full resize-none rounded-md border border-background-200 bg-background-100 px-3.5 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
        />
        <p className="text-right text-xs text-foreground-500">最多输入 500 字</p>
      </div>

      {/* 蜜罐字段 */}
      <div className="field-contact-alt" aria-hidden="true">
        <input
          type="text"
          name="contact_alt"
          tabIndex={-1}
          autoComplete="off"
          readOnly
          aria-hidden="true"
        />
      </div>

      {formError && (
        <p role="alert" className="rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-700">
          {formError}
        </p>
      )}
      {status && status.type === "success" && (
        <p role="status" className="rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
          <i className="ri-checkbox-circle-line mr-1.5" aria-hidden="true" />
          {status.message}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? (
          <>
            <i className="ri-loader-4-line animate-spin" aria-hidden="true" /> 提交中...
          </>
        ) : (
          <>
            <i className="ri-send-plane-fill" aria-hidden="true" /> 提交留言
          </>
        )}
      </button>
    </form>
  );
}